<template>
  <main class="room-experience" :class="`is-${phase}`">
    <router-link class="room-exit" to="/home" aria-label="Return home">H.W.</router-link>

    <section v-if="phase === 'idle' || phase === 'error'" class="room-entry">
      <p class="room-kicker">A temporary listening room</p>
      <h1>Leave something here.</h1>
      <p class="room-intro">A sentence, a question, an unfinished thought. A room of unfamiliar voices will gather, listen, and respond, then disappear.</p>
      <form class="fragment-form" @submit.prevent="begin">
        <label class="sr-only" for="room-fragment">Your fragment</label>
        <textarea
          id="room-fragment"
          ref="fragmentInput"
          v-model="fragment"
          maxlength="500"
          rows="3"
          placeholder="There was a blue chair in my grandmother’s kitchen."
          @keydown.meta.enter.prevent="begin"
          @keydown.ctrl.enter.prevent="begin"
        ></textarea>
        <div class="fragment-actions">
          <span>{{ fragment.length }} / 500</span>
          <button type="submit" :disabled="!fragment.trim()">Let it enter</button>
        </div>
      </form>
      <p v-if="phase === 'error'" class="room-error" role="alert">{{ errorMessage }}</p>
      <p class="room-note">Headphones make the space easier to hear.</p>
    </section>

    <section v-else class="room-stage" aria-live="polite">
      <div class="room-field" aria-hidden="true">
        <span class="room-boundary"></span>
        <span class="room-breath" :style="breathStyle"></span>
        <span
          v-for="speaker in roomSpeakers"
          :key="speaker.id"
          class="room-presence"
          :class="{ 'is-speaking': activeSpeakers.has(speaker.id) }"
          :style="speaker.style"
        ></span>
      </div>

      <div class="room-state">
        <blockquote>“{{ scene?.fragment || submittedFragment }}”</blockquote>

        <template v-if="phase === 'composing' || phase === 'rendering'">
          <div class="listening-mark" aria-hidden="true"><i></i><i></i><i></i></div>
          <p class="room-status">{{ statusText }}</p>
        </template>

        <template v-else-if="phase === 'ready'">
          <p class="room-status">The room is waiting.</p>
          <button class="room-listen" type="button" @click="listen">Enter the room</button>
          <p class="room-note">Give it a little space after the final voice.</p>
        </template>

        <template v-else-if="phase === 'playing'">
          <p class="room-status">{{ playbackStatus }}</p>
          <div class="room-progress" aria-hidden="true"><span :style="{ transform: `scaleX(${progress})` }"></span></div>
          <button class="room-leave" type="button" @click="endEarly">Leave the room</button>
        </template>

        <template v-else-if="phase === 'ended'">
          <p class="room-status">The room has gone.</p>
          <button class="room-listen" type="button" @click="reset">Leave another fragment</button>
        </template>
      </div>
    </section>

    <details v-if="debug && scene" class="room-debug">
      <summary>Scene diagnostics</summary>
      <pre>{{ debugText }}</pre>
    </details>

    <p class="room-disclosure">The words and performances are generated for this moment. They do not express the voice owners’ views or experiences.</p>
  </main>
</template>

<script>
import { composeRoom, synthesiseRoomLine } from '../utils/roomSite';
import { RoomAudioEngine } from '../utils/roomAudio';
import { activeRoomSpeakers, resolveRoomTimeline, ROOM_SPACES } from '../utils/roomTimeline.mjs';

export default {
  name: 'RoomExperience',
  data() {
    return {
      fragment: '',
      submittedFragment: '',
      phase: 'idle',
      errorCode: '',
      scene: null,
      timeline: null,
      engine: null,
      audioContext: null,
      controller: null,
      renderedLines: 0,
      totalLines: 0,
      elapsed: 0,
      activeSpeakers: new Set(),
      animationFrame: 0,
    };
  },
  computed: {
    debug() {
      return this.$route.query.debug === '1';
    },
    roomSpeakers() {
      return (this.scene?.speakers || []).map((speaker, index) => {
        const space = ROOM_SPACES[speaker.space] || ROOM_SPACES.center;
        return {
          ...speaker,
          style: {
            left: `${space.x}%`,
            top: `${space.y}%`,
            '--presence-delay': `${index * 170}ms`,
          },
        };
      });
    },
    statusText() {
      if (this.phase === 'composing') return 'The room is gathering.';
      if (!this.totalLines) return 'Voices are finding their places.';
      return `Voices are finding their places. ${this.renderedLines} of ${this.totalLines}`;
    },
    playbackStatus() {
      if (!this.timeline) return 'The room is here.';
      if (this.elapsed < this.timeline.opening - 0.08) return 'The room is here. Listen.';
      if (this.elapsed > this.timeline.spokenEnd && this.elapsed < this.timeline.duration) return 'The room is still here.';
      return 'Listen.';
    },
    progress() {
      return this.timeline ? Math.min(1, this.elapsed / this.timeline.duration) : 0;
    },
    breathStyle() {
      const intensity = this.activeSpeakers.size ? 1 : 0.42;
      return { opacity: intensity };
    },
    errorMessage() {
      if (this.errorCode === 'rate_limited') return 'The room needs a moment before another gathering. Please try again shortly.';
      if (this.errorCode === 'voices_unavailable') return 'The audio could not be prepared. Please try again.';
      return 'The room could not form this time. Your fragment is still here, so you can try again.';
    },
    debugText() {
      const timeline = this.timeline ? {
        opening: this.timeline.opening,
        spokenEnd: this.timeline.spokenEnd,
        duration: this.timeline.duration,
        events: this.timeline.events.map((event) => ({
          id: event.id,
          speaker_id: event.speaker_id,
          start: Number(event.start.toFixed(3)),
          duration: Number(event.duration.toFixed(3)),
          timing: event.timing,
          anchor_id: event.anchor_id,
        })),
      } : null;
      return JSON.stringify({ scene: this.scene, timeline }, null, 2);
    },
  },
  mounted() {
    document.body.classList.add('room-route-active');
  },
  beforeUnmount() {
    document.body.classList.remove('room-route-active');
    this.cancelWork();
    this.engine?.stop();
    if (this.audioContext && this.audioContext.state !== 'closed') this.audioContext.close();
  },
  methods: {
    ensureAudio() {
      if (!this.audioContext || this.audioContext.state === 'closed') {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
        this.engine = new RoomAudioEngine(this.audioContext);
      }
      this.audioContext.resume().catch(() => {});
    },
    cancelWork() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    },
    async begin() {
      const value = this.fragment.replace(/\s+/g, ' ').trim();
      if (!value || ['composing', 'rendering'].includes(this.phase)) return;
      this.cancelWork();
      this.engine?.stop();
      this.ensureAudio();
      this.controller = new AbortController();
      this.errorCode = '';
      this.submittedFragment = value;
      this.scene = null;
      this.timeline = null;
      this.renderedLines = 0;
      this.totalLines = 0;
      this.activeSpeakers = new Set();
      this.phase = 'composing';
      try {
        const scene = await composeRoom(value, this.controller.signal);
        this.scene = scene;
        this.totalLines = scene.utterances.length;
        this.phase = 'rendering';
        const clips = await this.renderScene(scene, this.controller.signal);
        const renderedSpeakers = new Set(scene.utterances
          .filter((utterance) => clips.has(utterance.id))
          .map((utterance) => utterance.speaker_id));
        if (clips.size < 7 || renderedSpeakers.size < 5) throw new Error('speech_unavailable');
        this.timeline = resolveRoomTimeline(scene, clips);
        if (this.timeline.events.length < 7) throw new Error('speech_unavailable');
        this.phase = 'ready';
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.errorCode = error?.message || 'room_unavailable';
        this.phase = 'error';
      } finally {
        this.controller = null;
      }
    },
    async renderScene(scene, signal) {
      const clips = new Map();
      const groups = new Map(scene.speakers.map((speaker) => [speaker.id, []]));
      for (const utterance of scene.utterances) groups.get(utterance.speaker_id)?.push(utterance);
      const jobs = scene.speakers.map(async (speaker) => {
        let voiceId = speaker.voice.id;
        let successful = 0;
        for (const utterance of groups.get(speaker.id) || []) {
          try {
            const rendered = await synthesiseRoomLine({
              text: utterance.text,
              voiceId,
              language: scene.language,
              allowReplacement: successful === 0,
            }, signal);
            voiceId = rendered.voiceId;
            if (voiceId !== speaker.voice.id) speaker.voice = { id: voiceId, title: 'Replacement public voice', languages: [scene.language] };
            const buffer = await this.engine.decode(rendered.bytes);
            clips.set(utterance.id, { buffer, duration: buffer.duration });
            successful += 1;
            this.renderedLines += 1;
          } catch (error) {
            if (error?.name === 'AbortError') throw error;
            console.warn('room_line_unavailable', utterance.id, error?.message || error);
          }
        }
      });
      await Promise.all(jobs);
      return clips;
    },
    async listen() {
      if (!this.timeline || !this.scene) return;
      this.phase = 'playing';
      this.elapsed = 0;
      await this.engine.play(this.scene, this.timeline, () => {
        this.phase = 'ended';
        this.activeSpeakers = new Set();
        cancelAnimationFrame(this.animationFrame);
      });
      this.updatePlayback();
    },
    updatePlayback() {
      if (this.phase !== 'playing') return;
      this.elapsed = this.engine.elapsed();
      this.activeSpeakers = activeRoomSpeakers(this.timeline, this.elapsed);
      this.animationFrame = requestAnimationFrame(this.updatePlayback);
    },
    endEarly() {
      this.engine?.stop();
      cancelAnimationFrame(this.animationFrame);
      this.activeSpeakers = new Set();
      this.phase = 'ended';
    },
    reset() {
      this.cancelWork();
      this.engine?.stop();
      this.scene = null;
      this.timeline = null;
      this.submittedFragment = '';
      this.phase = 'idle';
      this.$nextTick(() => this.$refs.fragmentInput?.focus());
    },
  },
};
</script>

<style scoped>
.room-experience {
  --room-paper: #e8ddca;
  --room-ink: #ded1bb;
  --room-muted: #9e927f;
  --room-line: rgba(232, 221, 202, 0.18);
  position: fixed;
  inset: 0;
  z-index: 1000;
  min-height: 100dvh;
  overflow: auto;
  display: grid;
  place-items: center;
  color: var(--room-ink);
  background:
    radial-gradient(circle at 50% 44%, rgba(119, 91, 55, 0.12), transparent 34rem),
    linear-gradient(165deg, #171612 0%, #0e0f0e 62%, #12100e 100%);
  font-family: "PT Serif", Georgia, serif;
  isolation: isolate;
}

.room-experience::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: -1;
  opacity: 0.16;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.22'/%3E%3C/svg%3E");
}

.room-exit {
  position: fixed;
  top: clamp(1.25rem, 3vw, 2.25rem);
  left: clamp(1.25rem, 4vw, 3.5rem);
  z-index: 3;
  color: var(--room-muted);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 1rem;
  letter-spacing: 0.12em;
  text-decoration: none;
  transition: color 180ms ease, transform 180ms var(--ease-out);
}

.room-exit:hover,
.room-exit:focus-visible {
  color: var(--room-paper);
  transform: translateY(-1px);
}

.room-entry {
  width: min(38rem, calc(100vw - 2.5rem));
  padding: 5rem 0 6rem;
}

.room-kicker {
  margin: 0 0 1.1rem;
  color: var(--room-muted);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.room-entry h1 {
  margin: 0;
  color: var(--room-paper);
  font-family: "PT Serif", Georgia, serif;
  font-size: clamp(2.35rem, 7vw, 4.7rem);
  font-weight: 400;
  line-height: 1.02;
  letter-spacing: -0.035em;
}

.room-intro {
  max-width: 32rem;
  margin: 1.5rem 0 3rem;
  color: var(--room-muted);
  font-size: clamp(1rem, 2.1vw, 1.16rem);
  line-height: 1.7;
}

.fragment-form {
  border-bottom: 1px solid var(--room-line);
}

.fragment-form textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 8.5rem;
  resize: vertical;
  padding: 0;
  border: 0;
  outline: 0;
  color: var(--room-ink);
  background: transparent;
  font: 400 clamp(1.25rem, 3vw, 1.65rem) / 1.55 "PT Serif", Georgia, serif;
  caret-color: #c5a36a;
}

.fragment-form textarea::placeholder {
  color: rgba(222, 209, 187, 0.3);
}

.fragment-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0 1.15rem;
  color: #756d61;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
}

.fragment-actions button,
.room-listen,
.room-leave {
  border: 0;
  color: var(--room-paper);
  background: transparent;
  font: 600 0.78rem / 1 "Quattrocento Sans", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.fragment-actions button {
  padding: 0.75rem 0 0.75rem 1.2rem;
}

.fragment-actions button::after {
  content: '  →';
  display: inline-block;
  margin-left: 0.55rem;
  transition: transform 180ms var(--ease-out);
}

.fragment-actions button:hover::after { transform: translateX(0.3rem); }
.fragment-actions button:disabled { opacity: 0.3; cursor: default; }

.room-note,
.room-error {
  margin: 1.25rem 0 0;
  color: #82796c;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  line-height: 1.5;
}

.room-error { color: #c3a887; }

.room-stage {
  position: relative;
  width: min(64rem, 100vw);
  min-height: 100dvh;
  display: grid;
  place-items: center;
}

.room-field {
  position: absolute;
  inset: clamp(4rem, 9vw, 7rem) clamp(1rem, 7vw, 5rem);
  overflow: hidden;
}

.room-boundary {
  position: absolute;
  inset: 14% 5% 5%;
  border: 1px solid rgba(232, 221, 202, 0.08);
  border-radius: 50%;
  transform: perspective(34rem) rotateX(62deg);
  box-shadow: inset 0 0 7rem rgba(187, 147, 87, 0.035);
}

.room-breath {
  position: absolute;
  left: 50%;
  top: 48%;
  width: min(55vw, 30rem);
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(190, 153, 98, 0.08), transparent 67%);
  transform: translate(-50%, -50%);
  transition: opacity 900ms ease;
  animation: room-breathe 7s ease-in-out infinite;
}

.room-presence {
  position: absolute;
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 50%;
  background: rgba(232, 221, 202, 0.18);
  box-shadow: 0 0 0 rgba(210, 175, 117, 0);
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.6);
  animation: presence-arrive 1.2s var(--ease-out) var(--presence-delay) forwards;
  transition: background 220ms ease, box-shadow 300ms ease, transform 300ms var(--ease-out);
}

.room-presence.is-speaking {
  background: rgba(242, 220, 180, 0.9);
  box-shadow: 0 0 1.3rem 0.55rem rgba(190, 145, 78, 0.18);
  transform: translate(-50%, -50%) scale(1.45);
}

.room-state {
  position: relative;
  z-index: 2;
  width: min(36rem, calc(100vw - 3rem));
  text-align: center;
}

.room-state blockquote {
  margin: 0 auto 2.8rem;
  padding: 0;
  border: 0;
  color: rgba(222, 209, 187, 0.54);
  font-size: clamp(0.95rem, 2.3vw, 1.2rem);
  font-style: italic;
  line-height: 1.7;
}

.room-status {
  margin: 1.1rem 0;
  color: var(--room-paper);
  font-size: clamp(1.3rem, 3vw, 1.75rem);
  line-height: 1.4;
}

.listening-mark {
  height: 1.8rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.38rem;
}

.listening-mark i {
  width: 1px;
  height: 0.35rem;
  background: #b39569;
  animation: listening-line 1.35s ease-in-out infinite;
}
.listening-mark i:nth-child(2) { animation-delay: 150ms; }
.listening-mark i:nth-child(3) { animation-delay: 300ms; }

.room-listen {
  margin-top: 1rem;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid rgba(232, 221, 202, 0.36);
}

.room-leave {
  margin-top: 1.3rem;
  padding: 0.8rem;
  color: #83796c;
  font-size: 0.66rem;
}

.room-progress {
  width: min(16rem, 70vw);
  height: 1px;
  margin: 1.8rem auto 0;
  overflow: hidden;
  background: rgba(232, 221, 202, 0.1);
}

.room-progress span {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left;
  background: rgba(210, 176, 122, 0.6);
}

.room-disclosure {
  position: fixed;
  right: clamp(1rem, 3vw, 2rem);
  bottom: clamp(0.85rem, 2vw, 1.5rem);
  z-index: 3;
  width: min(31rem, calc(100vw - 2rem));
  margin: 0;
  color: rgba(158, 146, 127, 0.58);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  line-height: 1.4;
  text-align: right;
}

.room-debug {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  z-index: 5;
  width: min(36rem, calc(100vw - 2rem));
  max-height: 44vh;
  overflow: auto;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #d8d2c7;
  background: rgba(10, 10, 9, 0.92);
  font: 0.68rem/1.45 "Source Code Pro", monospace;
}

.room-debug pre { margin: 0.8rem 0 0; color: inherit; background: transparent; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }

@keyframes listening-line {
  0%, 100% { height: 0.35rem; opacity: 0.35; }
  50% { height: 1.35rem; opacity: 0.9; }
}

@keyframes room-breathe {
  0%, 100% { transform: translate(-50%, -50%) scale(0.94); }
  50% { transform: translate(-50%, -50%) scale(1.04); }
}

@keyframes presence-arrive {
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

@media (max-width: 38rem) {
  .room-entry { padding-top: 6rem; }
  .room-field { inset: 5rem 0.5rem 4.5rem; }
  .room-disclosure { text-align: left; font-size: 0.58rem; }
  .room-state blockquote { margin-bottom: 2rem; }
}

@media (prefers-reduced-motion: reduce) {
  .room-breath,
  .listening-mark i,
  .room-presence { animation: none; }
  .room-presence { opacity: 1; transform: translate(-50%, -50%); }
}
</style>
